import { useState } from 'react'

import { useUserOptions } from '#/entities/user'
import type { useCreateToolbarItem } from '#/features/shipment-toolbar'
import { toTaskApiInput } from '#/features/shipment-toolbar'
import type { TaskValue } from '#/shared/ui/task-editor'

import { startOfToday } from './helpers'

function makeDefaultTask(): TaskValue {
  return {
    task: '',
    type: 'phone',
    date: startOfToday(),
    time: '00:00',
    priority: 'medium',
    users: [],
  }
}

interface TaskSliceDeps {
  shipmentId: string | undefined
  createTask: ReturnType<typeof useCreateToolbarItem>['createTask']
  isPending: boolean
  closeTab: () => void
}

export function useTaskSlice({
  shipmentId,
  createTask,
  isPending,
  closeTab,
}: TaskSliceDeps) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState<TaskValue>(makeDefaultTask)

  const {
    options: userOptions,
    isLoading: usersLoading,
    searchValue: userSearch,
    onSearchChange: onUserSearchChange,
  } = useUserOptions()

  const reset = () => {
    setValue(makeDefaultTask())
    setOpen(false)
    closeTab()
  }

  const onSave = async (next: TaskValue) => {
    if (!shipmentId || isPending) return
    if (!next.task.trim()) return
    try {
      await createTask(toTaskApiInput(next))
    } catch {
      return // onError already surfaced the failure; keep the draft
    }
    reset()
  }

  return {
    open,
    value,
    onCreate: () => setOpen(true),
    onCancel: reset,
    onSave,
    onChange: setValue,
    isSaving: isPending,
    canSave: Boolean(shipmentId),
    userOptions,
    usersLoading,
    userSearch,
    onUserSearchChange,
  }
}
